import React, {useContext, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {useKeycloak} from '@react-keycloak/web';

import {Button, message, Table, Tag} from 'antd';
import {FcCheckmark} from 'react-icons/all';
import {handleError} from 'utils/MessageUtils';
import {X_TOTAL_COUNT} from 'shared/constants/AppConst';
import {EQUAL} from 'shared/constants/QueryModes';
import {PractitionerContext} from '../PractitionerContext';
import {findPractitionerRoles} from 'services/PractitionerRoleService';
import {findOrganizationById} from 'services/OrganizationService';

const RoleList = () => {
  const {keycloak} = useKeycloak();
  const intl = useIntl();
  const navigate = useNavigate();

  const headers = {
    Authorization: `Bearer ${keycloak?.token}`,
    'Accept-Language': intl.locale,
  };

  const practitionerContext = useContext(PractitionerContext);
  const {selectedPractitioner} = practitionerContext;

  const [roles, setRoles] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    position: ['bottomCenter'],
  });

  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);

  const loadData = (params) => {
    setLoading(true);

    const filters = params.filters
      ? Object.keys(params.filters).map((key) => ({
          fields: key,
          value: params.filters[key],
        }))
      : [];

    filters.push({
      fields: 'practitioner.id',
      mode: EQUAL,
      value: selectedPractitioner.id,
    });

    findPractitionerRoles(headers, {
      pagination: params.pagination,
      filters,
      sorters: params.sorters,
    })
      .then((res) => {
        Promise.all(
          res.data.map((role) => {
            return findOrganizationById(headers, role.organization.id).then(
              (res) => {
                return {...role, organization: res.data};
              },
            );
          }),
        ).then((values) => {
          setRoles(values);
        });

        setPagination({
          ...params.pagination,
          total: res.headers[X_TOTAL_COUNT]
            ? parseInt(res.headers[X_TOTAL_COUNT])
            : 0,
        });
      })
      .catch((error) => handleError(message, error))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData({pagination});
  }, []);

  useEffect(() => {
    if (reloading) {
      loadData({pagination});
      setReloading(false);
    }
  }, [reloading]);

  const onView = (id) => {
    navigate(`/health/organizations/${id}`);
  };

  const onChange = (pagination, filters, sorter) => {
    loadData({pagination, filters, sorters: [sorter]});
  };

  const columns = [
    {
      title: intl.messages['organization'],
      dataIndex: 'organization',
      key: 'organization',
      render: (organization) => (
        <Button type='link' onClick={() => onView(organization.id)}>
          {organization.name}
        </Button>
      ),
    },
    {
      title: intl.messages['common.enabled'],
      dataIndex: 'active',
      key: 'active',
      render: (active) => <span>{active ? <FcCheckmark /> : <></>}</span>,
    },
    {
      title: intl.messages['practitioner.role.startTime'],
      dataIndex: 'period',
      key: 'period',
      render: (period) => <span>{period?.start}</span>,
    },
    {
      title: intl.messages['practitioner.role.endTime'],
      dataIndex: 'period',
      key: 'period',
      render: (period) => <span>{period?.end}</span>,
    },
    {
      title: intl.messages['practitioner.role'],
      dataIndex: 'codes',
      key: 'codes',
      render: (codes) => (
        <span>
          {codes.map((code) => (
            <Tag color='blue' key={code}>
              {intl.messages[`practitioner.role.${code.toLowerCase()}`]}
            </Tag>
          ))}
        </span>
      ),
    },
    {
      title: intl.messages['common.modifiedDate'],
      dataIndex: 'modifiedDate',
      key: 'modifiedDate',
    },
  ];

  return (
    <>
      <Table
        loading={loading}
        columns={columns}
        dataSource={roles}
        rowKey={(record) => record.id}
        pagination={pagination}
        onChange={onChange}
        scroll={{x: 'auto'}}
      />
    </>
  );
};

export default RoleList;
